import { Alert, Box, Button, Group, List, Paper, Skeleton, Stack, Text, Title } from '@mantine/core';
import { IconAlertCircle, IconDownload } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { offlineKitApi, type OfflineKitStatus } from '@/services/offline-kit.api';
import { OFFLINE_KIT_STATUS_KEY, useOfflineKitStatus } from '@/components/OfflineKit/useOfflineKitStatus';
import { STALE_AFTER_DAYS } from '@/components/OfflineKit/staleness';
import { formatBytes, formatDate, pluralize } from '@/components/OfflineKit/format';

const NAVY = '#415A91';
const MUTED = '#4E5A6B';

/**
 * Offline Kit: every published flow as files that run in a browser with no
 * network, for the team to follow playbooks while the app is unreachable.
 *
 * Deliberately one button and no flow picker: the flow someone deselects in
 * June is the one they need in November. The pre-flight summary is shown
 * before the button so problems are read before the download, not after.
 */
export function OfflineKitSection({ isAdmin }: { isAdmin: boolean }) {
  const queryClient = useQueryClient();
  const { data: status, isLoading, error, staleness } = useOfflineKitStatus(isAdmin);

  const download = useMutation({
    mutationFn: offlineKitApi.download,
    onSuccess: ({ fileName, skippedImages }) => {
      queryClient.invalidateQueries({ queryKey: OFFLINE_KIT_STATUS_KEY });
      notifications.show(
        skippedImages > 0
          ? {
              color: 'yellow',
              title: 'Offline Kit downloaded, with missing images',
              message: `${pluralize(skippedImages, 'image')} could not be included in ${fileName}. The flows are all there; try downloading again later for a complete copy.`,
              autoClose: false,
            }
          : {
              color: 'green',
              title: 'Offline Kit downloaded',
              message: `${fileName}. Keep it in a folder that syncs to this device, not only in the cloud.`,
            },
      );
    },
    onError: async (err: any) => {
      notifications.show({
        color: 'red',
        title: 'The Offline Kit was not downloaded',
        message: await errorMessage(err),
      });
    },
  });

  return (
    <Paper withBorder p="lg" radius="md">
      <Title order={4} mb="xs">Offline Kit</Title>
      <Text size="xs" c="dimmed">
        A zip of every published flow as files that open in a web browser with no
        internet connection, so your team can follow its playbooks while the app is
        unreachable. Nothing entered offline is saved or sent, and email, SMS and
        webhook steps are described but never run. Recipients and credentials are
        left out, so the kit is safe to store on laptops and shared drives.
      </Text>
      <Text size="xs" c="dimmed" mt={6}>
        It is not a backup: it cannot be restored into the app.
      </Text>

      {!isAdmin ? (
        <Text size="sm" c={MUTED} mt="md">Only admins can download the Offline Kit.</Text>
      ) : isLoading ? (
        <Stack gap="xs" mt="md">
          <Skeleton height={14} width="60%" />
          <Skeleton height={14} width="40%" />
        </Stack>
      ) : error || !status ? (
        <Alert icon={<IconAlertCircle size={16} />} color="red" mt="md">
          Could not load the Offline Kit status. Reload the page to try again.
        </Alert>
      ) : (
        <Stack gap="md" mt="md">
          <LastDownload status={status} />

          {staleness?.level === 'info' && (
            <Text size="sm" c={MUTED}>
              {pluralize(staleness.changedFlows, 'flow has', 'flows have')} changed since then.
            </Text>
          )}
          {staleness?.level === 'warning' && staleness.reason === 'old' && (
            <Text size="sm" c="orange.8">
              This kit is {staleness.ageDays} days old. Download a fresh one at least every{' '}
              {STALE_AFTER_DAYS} days.
            </Text>
          )}

          <Preflight status={status} />

          <Group justify="space-between" align="center">
            <Text size="xs" c="dimmed" maw={420}>
              Download again after any significant change, and at least every {STALE_AFTER_DAYS} days.
            </Text>
            <Button
              leftSection={<IconDownload size={16} />}
              loading={download.isPending}
              disabled={!status.preflight.shellAvailable || status.preflight.flows.length === 0}
              style={{ backgroundColor: NAVY }}
              onClick={() => download.mutate()}
            >
              Download Offline Kit
            </Button>
          </Group>
        </Stack>
      )}
    </Paper>
  );
}

function LastDownload({ status }: { status: OfflineKitStatus }) {
  if (!status.lastExport) {
    return <Text size="sm" fw={500}>Never downloaded.</Text>;
  }

  const { at, by, downloadId } = status.lastExport;
  return (
    <Box>
      <Text size="sm" fw={500}>
        Last downloaded {formatDate(at)} by {by.name}
      </Text>
      <Text size="xs" c="dimmed">Download ID {downloadId}</Text>
    </Box>
  );
}

/** What the download will contain, and anything worth knowing first. */
function Preflight({ status }: { status: OfflineKitStatus }) {
  const { flows, totalEstimatedBytes, shellAvailable, limits } = status.preflight;

  if (flows.length === 0) {
    return (
      <Text size="sm" c={MUTED}>
        There are no published flows yet. Publish a flow to include it in the kit.
      </Text>
    );
  }

  const warnings: string[] = [];
  for (const flow of flows) {
    if (flow.size === 'over-limit') {
      warnings.push(`${flow.name} is too large for one file. Some of its images will be left out; its text is always included.`);
    } else if (flow.size === 'near-limit') {
      warnings.push(`${flow.name} is close to the ${formatBytes(limits.flowMaxBytes)} size limit for one flow.`);
    }
    if (flow.missingImageCount > 0) {
      warnings.push(`${flow.name}: ${pluralize(flow.missingImageCount, 'image')} could not be found and will be missing.`);
    }
    if (flow.unsupported.length > 0) {
      const types = [...new Set(flow.unsupported.map((n) => n.type))].join(', ');
      warnings.push(`${flow.name} has steps that cannot run offline (${types}). The flow will stop there.`);
    }
  }
  if (totalEstimatedBytes > limits.emailBytes) {
    warnings.push('The kit may be too large to send by email. A folder that syncs to your devices is a better home for it anyway.');
  }

  return (
    <Stack gap="xs">
      {!shellAvailable && (
        <Alert icon={<IconAlertCircle size={16} />} color="red">
          Downloads are unavailable on this server: the offline runner has not been built.
        </Alert>
      )}

      <Text size="sm" c={MUTED}>
        {pluralize(flows.length, 'published flow')} will be included, about{' '}
        {formatBytes(totalEstimatedBytes)} before compression.
      </Text>

      {warnings.length > 0 && (
        <Alert icon={<IconAlertCircle size={16} />} color="yellow" variant="light" title="Before you download">
          <List size="sm" spacing={4}>
            {warnings.map((warning) => (
              <List.Item key={warning}>{warning}</List.Item>
            ))}
          </List>
        </Alert>
      )}
    </Stack>
  );
}

/** A failed blob request carries its JSON error body as a Blob. */
async function errorMessage(err: any): Promise<string> {
  const data = err?.response?.data;
  if (data instanceof Blob) {
    try {
      return JSON.parse(await data.text())?.message ?? 'Something went wrong.';
    } catch {
      return 'Something went wrong.';
    }
  }
  return data?.message || err?.message || 'Something went wrong.';
}
